import api from './client';

export interface KpiStat {
  value: number;
  /** Pre-formatted display value (e.g. "$4,280"); falls back to value. */
  valueLabel?: string;
  change: string;
  up: boolean;
}

export interface DashboardKpis {
  totalListings: KpiStat;
  activeAgents: KpiStat;
  pendingInquiries: KpiStat;
  monthRevenue: KpiStat;
}

export interface DashboardQuickActions {
  pendingListings: number;
  inquiries: number;
  newAgents: number;
  serviceOrders: number;
}

export interface RevenueBreakdownItem {
  label: string;
  amount: number;
  amountLabel: string;
  pct: number;
}

export interface ActivityItem {
  icon: string;
  color: 'brand' | 'green' | string;
  text: string;
  detail: string;
  link: string;
  time: string;
}

export interface DashboardSummary {
  kpis: DashboardKpis;
  quickActions: DashboardQuickActions;
  revenueBreakdown: RevenueBreakdownItem[];
  activity: ActivityItem[];
}

interface SummaryResponse extends DashboardSummary {
  status: number;
}

// ─── Agent dashboard ────────────────────────────────────────────────────────

export interface AgentStat {
  value: number;
  valueLabel?: string;
  change: string;
  up: boolean;
}

export interface AgentDashboardStats {
  activeListings: AgentStat;
  totalInquiries: AgentStat;
  listingViews: AgentStat;
  plan: {
    name: string;
    active: boolean;
    status?: string | null;
    /** e.g. "Free trial · ends Oct 30" */
    statusLabel?: string;
  };
}

export interface AgentRecentInquiry {
  id: number;
  name: string;
  property: string;
  time: string;
  isNew: boolean;
}

export interface AgentTopListing {
  id: number;
  addr: string;
  views: number;
  inquiries: number;
  status: 'active' | 'inactive';
}

export interface AgentDashboardSummary {
  stats: AgentDashboardStats;
  newInquiries: number;
  recentInquiries: AgentRecentInquiry[];
  topListings: AgentTopListing[];
}

interface AgentSummaryResponse extends AgentDashboardSummary {
  status: number;
}

/** GET /api/dashboard/agent-summary — data scoped to the logged-in agent. */
export const getAgentDashboardSummary =
  async (): Promise<AgentDashboardSummary> => {
    const res = await api.get<AgentSummaryResponse>('/dashboard/agent-summary');
    return {
      stats: res.data.stats,
      newInquiries: res.data.newInquiries,
      recentInquiries: res.data.recentInquiries,
      topListings: res.data.topListings,
    };
  };

/** GET /api/dashboard/summary — admin aggregate. */
export const getDashboardSummary = async (): Promise<DashboardSummary> => {
  const res = await api.get<SummaryResponse>('/dashboard/summary');
  return {
    kpis: res.data.kpis,
    quickActions: res.data.quickActions,
    revenueBreakdown: res.data.revenueBreakdown,
    activity: res.data.activity,
  };
};

// ─── Analytics (dashboard graphs) ────────────────────────────────────────────

/** One point in a daily time series. */
export interface SeriesPoint {
  date: string;
  value: number;
}

/** One category in a status distribution. */
export interface DistributionPoint {
  label: string;
  value: number;
}

export interface DashboardAnalytics {
  days: number;
  from: string;
  to: string;
  series: {
    registrations: SeriesPoint[];
    listings: SeriesPoint[];
    inquiries: SeriesPoint[];
    applications: SeriesPoint[];
    orders: SeriesPoint[];
    revenue: SeriesPoint[];
  };
  distributions: {
    listingsByStatus: DistributionPoint[];
    inquiriesByStatus: DistributionPoint[];
    ordersByStatus: DistributionPoint[];
  };
}

interface AnalyticsResponse extends DashboardAnalytics {
  status: number;
}

/** GET /api/dashboard/analytics — 30-day series + status distributions. */
export const getDashboardAnalytics = async (): Promise<DashboardAnalytics> => {
  const res = await api.get<AnalyticsResponse>('/dashboard/analytics');
  return {
    days: res.data.days,
    from: res.data.from,
    to: res.data.to,
    series: res.data.series,
    distributions: res.data.distributions,
  };
};
